"use client";

import * as React from "react";

import { Combobox as ComboboxPrimitive } from "@base-ui-components/react/combobox";

import { cn } from "@/lib/utils";

function ComboboxRoot<Value, Multiple extends boolean | undefined = false>(
  props: Parameters<typeof ComboboxPrimitive.Root<Value, Multiple>>[0],
) {
  return <ComboboxPrimitive.Root {...props} />;
}

function ComboboxTrigger({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Trigger>) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn("cursor-pointer", className)}
      {...props}
    />
  );
}

function ComboboxValue({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Value>) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxPortal({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Portal>) {
  return <ComboboxPrimitive.Portal {...props} />;
}

function ComboboxPositioner({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Positioner>) {
  return (
    <ComboboxPrimitive.Positioner
      data-slot="combobox-positioner"
      sideOffset={sideOffset}
      className={cn("z-50 outline-none", className)}
      {...props}
    />
  );
}

function ComboboxPopup({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Popup>) {
  return (
    <ComboboxPrimitive.Popup
      data-slot="combobox-popup"
      className={cn(
        "bg-white border border-input-border rounded-none shadow-bevel-lg w-full max-h-80 flex flex-col overflow-hidden",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxInput({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Input>) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-input"
      className={cn(
        "w-full border-b border-input-border px-3 py-2 outline-none",
        "theme-universal:font-body theme-universal:text-input-text theme-universal:text-(length:--ul-theme-font-body-text-size)",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxList({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.List>) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn("overflow-y-auto flex flex-col p-1", className)}
      {...props}
    />
  );
}

function ComboboxItem({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Item>) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "flex items-center justify-between gap-2 px-3 py-2 rounded-md cursor-pointer outline-none data-highlighted:bg-gray-100",
        "theme-universal:font-body theme-universal:text-input-text theme-universal:text-(length:--ul-theme-font-body-text-size)",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxEmpty({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Empty>) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "px-3 py-2 theme-universal:font-body theme-universal:text-input-labels theme-universal:text-(length:--ul-theme-font-body-text-size)",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxIcon({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Icon>) {
  return <ComboboxPrimitive.Icon data-slot="combobox-icon" {...props} />;
}

export {
  ComboboxEmpty as Empty,
  ComboboxIcon as Icon,
  ComboboxInput as Input,
  ComboboxItem as Item,
  ComboboxList as List,
  ComboboxPopup as Popup,
  ComboboxPortal as Portal,
  ComboboxPositioner as Positioner,
  ComboboxRoot as Root,
  ComboboxTrigger as Trigger,
  ComboboxValue as Value,
};
